import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { PaymentDialog } from "@/components/PaymentDialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tent, Loader2 } from "lucide-react";
import { announceBooking } from "@/components/FirstVisitLocation";
import { toast } from "sonner";

const LEGS = { none: 0, pickup: 1, dropoff: 1, both: 2 };
const dayLabel = (iso) => new Date(iso + "T12:00").toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" });

export default function Camps() {
  const [params] = useSearchParams();
  const [data, setData] = useState({ camps: [] });
  const [settings, setSettings] = useState(null);
  const [schools, setSchools] = useState([]);
  const [kids, setKids] = useState([]);
  const [campId, setCampId] = useState("");
  const [childId, setChildId] = useState("");
  const [option, setOption] = useState("");
  const [dates, setDates] = useState([]);
  const [transport, setTransport] = useState("none");
  const [tinfo, setTinfo] = useState({});
  const [promo, setPromo] = useState("");
  const [ebValid, setEbValid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    (async () => {
      const [c, k, s, sch] = await Promise.all([
        api.get("/camps"), api.get("/children"), api.get("/public/settings"), api.get("/schools"),
      ]);
      setData(c.data); setKids(k.data); setSettings(s.data); setSchools(sch.data);
      const wanted = params.get("camp");
      const open = c.data.camps.find((x) => x.id === wanted && x.status !== "closed") || c.data.camps.find((x) => x.status !== "closed");
      if (open) setCampId(open.id);
      if (k.data[0]) setChildId(k.data[0].id);
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const camp = data.camps.find((c) => c.id === campId);
  const selChild = kids.find((k) => k.id === childId);
  const need = option ? parseInt(option) : 0;

  const chooseOption = (n) => {
    setOption(`${n}day`);
    if (camp && n >= camp.dates.length) setDates([...camp.dates]);
    else setDates([]);
  };
  const toggleDate = (d) => {
    if (dates.includes(d)) return setDates(dates.filter((x) => x !== d));
    if (dates.length >= need) return toast.error(`Pick exactly ${need} days for this option`);
    setDates([...dates, d]);
  };

  const checkPromo = async () => {
    const code = promo.trim();
    if (!code) return setEbValid(false);
    try { const { data: r } = await api.get(`/early-bird/check?code=${encodeURIComponent(code)}`); setEbValid(!!r.valid); if (!r.valid && r.reason) toast.error(r.reason); }
    catch { setEbValid(false); }
  };

  const pricing = settings?.pricing || {};
  const gold = selChild?.is_gold;
  const useFounding = data.founding_active || ebValid;
  const dayRate = camp?.day_price ? Number(camp.day_price) : (useFounding ? (pricing.founding_daily ?? pricing.daily) : gold ? (pricing.gold_daily ?? pricing.daily) : pricing.daily) || 0;
  const tprice = data.transport_price_each_way || settings?.transport?.price_each_way || 12;
  const transportCost = camp?.transport_enabled ? (LEGS[transport] || 0) * tprice * dates.length : 0;
  const estimate = dayRate * dates.length + transportCost;

  const register = async () => {
    if (!camp) return toast.error("Select a camp");
    if (!childId) return toast.error("Select an explorer");
    if (!selChild?.waiver?.signed) return toast.error("Please sign the waiver for this explorer first (My Explorers).");
    if (!option) return toast.error("Choose a 3-day or 5-day option");
    if (dates.length !== need) return toast.error(`Please select exactly ${need} camp days`);
    setBusy(true);
    try {
      const { data: r } = await api.post(`/camps/${camp.id}/register`, { child_id: childId, option_key: option, dates, transport: camp.transport_enabled ? transport : "none", transport_info: tinfo, promo_code: promo || null });
      setOrder(r);
      announceBooking();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <h1 className="font-display font-extrabold text-2xl text-navy flex items-center gap-2"><Tent className="w-6 h-6 text-turquoise" /> Camps</h1>
      <p className="text-slate-500">Register your explorer for a school-break adventure camp.</p>

      {data.camps.length === 0 && <p className="text-slate-400 mt-6" data-testid="dash-camps-empty">No camps are open right now. Check back soon!</p>}

      {data.camps.length > 0 && (
        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
              <div>
                <Label className="text-xs">Camp</Label>
                <Select value={campId} onValueChange={(v) => { setCampId(v); setOption(""); setDates([]); }}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="camp-select"><SelectValue placeholder="Select a camp" /></SelectTrigger>
                  <SelectContent>{data.camps.map((c) => <SelectItem key={c.id} value={c.id} disabled={c.status === "closed"}>{c.emoji} {c.name}{c.status === "closed" ? " (closed)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Explorer</Label>
                <Select value={childId} onValueChange={setChildId}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="camp-child-select"><SelectValue placeholder="Select explorer" /></SelectTrigger>
                  <SelectContent>{kids.map((k) => <SelectItem key={k.id} value={k.id}>{k.preferred_name || k.first_name} {k.last_name}</SelectItem>)}</SelectContent>
                </Select>
                {kids.length === 0 && <p className="text-coral text-sm mt-2">Add an explorer first from My Explorers.</p>}
                {selChild && !selChild.waiver?.signed && <p className="text-coral text-sm mt-2 font-semibold" data-testid="camp-waiver-warning">⚠ Please sign the waiver & media consent for {selChild.preferred_name || selChild.first_name} in <b>My Explorers</b> before registering.</p>}
              </div>
            </div>

            {camp && (
              <>
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <Label className="text-xs mb-2 block">Camp option</Label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {camp.day_options.map((n) => (
                      <button key={n} onClick={() => chooseOption(n)} data-testid={`camp-option-${n}`}
                        className={`text-left rounded-xl border-2 p-3 transition-all ${option === `${n}day` ? "border-turquoise bg-turquoise/10" : "border-slate-200"}`}>
                        <p className="font-display font-bold text-navy text-sm">{n === 1 ? "Single Day" : n >= camp.dates.length ? `Full Camp (${n} days)` : `${n}-Day Camp`}</p>
                        <p className="font-bold text-turquoise mt-1">${dayRate * n}<span className="text-xs text-slate-400 font-normal"> total</span></p>
                      </button>
                    ))}
                  </div>
                </div>

                {option && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <Label className="text-xs mb-2 block">{need >= camp.dates.length ? "All camp days included" : `Pick your ${need} days (${dates.length}/${need})`}</Label>
                    <div className="flex flex-wrap gap-2">
                      {camp.dates.map((d) => {
                        const on = dates.includes(d);
                        const locked = need >= camp.dates.length;
                        return (
                          <button key={d} disabled={locked} onClick={() => toggleDate(d)} data-testid={`camp-day-${d}`}
                            className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${on ? "bg-turquoise text-navy border-turquoise" : "bg-white border-slate-200 text-navy hover:border-turquoise"} ${locked ? "opacity-90 cursor-default" : ""}`}>
                            {dayLabel(d)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {camp.transport_enabled && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="camp-transport-card">
                    <Label className="text-xs mb-1 block">🚐 Camp transport — ${tprice} per child, each way (applies to each camp day)</Label>
                    <p className="text-[11px] text-slate-400 mb-2">Camps run on no-school days, so our mini-bus collects from and returns to your home.</p>
                    <div className="grid sm:grid-cols-2 gap-2 mt-1">
                      {[["none", "No transport (I'll drop off & collect)", 0], ["pickup", "Morning Home Pick-Up", tprice], ["dropoff", "Evening Home Drop-Off", tprice], ["both", "Pick-Up + Drop-Off", tprice * 2]].map(([k, l, c]) => (
                        <button key={k} onClick={() => setTransport(k)} data-testid={`camp-transport-${k}`} className={`text-left rounded-xl border-2 p-3 transition-all ${transport === k ? "border-turquoise bg-turquoise/10" : "border-slate-200"}`}>
                          <p className="font-semibold text-navy text-sm">{l}</p><p className="text-turquoise font-bold text-sm">{c ? `$${c}/day` : "Free"}</p>
                        </button>
                      ))}
                    </div>
                    {(transport === "pickup" || transport === "both") && (
                      <div className="mt-3 space-y-2">
                        <Label className="text-xs">Morning pick-up details</Label>
                        <Input placeholder="Home pick-up address" onChange={(e) => setTinfo({ ...tinfo, pickup_address: e.target.value })} className="rounded-xl" data-testid="camp-pickup-addr" />
                        <Input placeholder="Preferred pick-up time (e.g. 7:45 AM)" onChange={(e) => setTinfo({ ...tinfo, pickup_time: e.target.value })} className="rounded-xl" data-testid="camp-pickup-time" />
                      </div>
                    )}
                    {(transport === "dropoff" || transport === "both") && (
                      <div className="mt-3 space-y-2">
                        <Label className="text-xs">Evening drop-off preference</Label>
                        <div className="grid gap-2">
                          {[["collected_stop", "An adult will meet the bus at our stop"], ["walk_home", "May walk home from the drop-off point"], ["door", "Bus can reach our door"]].map(([k, l]) => (
                            <button type="button" key={k} onClick={() => setTinfo({ ...tinfo, dropoff_preference: k })} data-testid={`camp-dropoff-${k}`} className={`text-left rounded-xl border-2 px-3 py-2 text-sm transition-colors ${tinfo.dropoff_preference === k ? "border-turquoise bg-turquoise/10 text-navy font-semibold" : "border-slate-200 text-slate-600 hover:border-turquoise/50"}`}>{l}</button>
                          ))}
                        </div>
                        <Input placeholder="Approved drop-off address" onChange={(e) => setTinfo({ ...tinfo, dropoff_address: e.target.value })} className="rounded-xl" data-testid="camp-dropoff-addr" />
                        <Input placeholder="Who will receive the child (name)" onChange={(e) => setTinfo({ ...tinfo, receiver_name: e.target.value })} className="rounded-xl" data-testid="camp-dropoff-receiver" />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="bg-navy text-white rounded-2xl p-6 h-fit lg:sticky lg:top-20" data-testid="camp-summary">
            <p className="font-display font-bold text-lg">Camp Summary</p>
            <div className="mt-4 space-y-2 text-sm text-white/70">
              <div className="flex justify-between"><span>Camp</span><span className="text-white font-semibold text-right">{camp?.name || "—"}</span></div>
              <div className="flex justify-between"><span>Option</span><span className="text-white font-semibold">{option ? `${need} days` : "—"}</span></div>
              <div className="flex justify-between"><span>Days chosen</span><span className="text-white font-semibold">{dates.length}</span></div>
              <div className="flex justify-between"><span>Per day</span><span className="text-white">${dayRate}</span></div>
              {transportCost > 0 && <div className="flex justify-between"><span>Transport</span><span className="text-white">${transportCost}</span></div>}
            </div>
            <div className="mt-4">
              <Label className="text-xs text-white/60">Early Bird code</Label>
              <Input value={promo} onChange={(e) => { setPromo(e.target.value); setEbValid(false); }} onBlur={checkPromo} className="rounded-xl bg-white/10 border-white/20 text-white mt-1" data-testid="camp-promo-input" />
              {ebValid && <p className="text-[11px] text-emerald-300 mt-1 font-semibold" data-testid="camp-promo-ok">✅ Early Bird price applied!</p>}
            </div>
            <div className="flex justify-between items-center mt-5 pt-4 border-t border-white/10">
              <span className="text-white/70">Estimated total</span>
              <span className="font-display font-extrabold text-2xl text-gold">${estimate.toFixed(2)}</span>
            </div>
            <Button onClick={register} disabled={busy} className="w-full mt-4 bg-gold hover:bg-gold/90 text-navy font-bold rounded-full" data-testid="camp-register-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Register for Camp"}
            </Button>
            <p className="text-[11px] text-white/50 mt-2 text-center">{settings?.payment_due_date ? `Pay by ${new Date(settings.payment_due_date + "T12:00").toLocaleDateString("en", { month: "long", day: "numeric" })} (cash or WOI) to secure the spot.` : "Pay anytime by cash or WOI to secure the spot."}</p>
          </div>
        </div>
      )}

      <PaymentDialog order={order} open={!!order} onClose={() => { setOrder(null); setOption(""); setDates([]); }} />
    </div>
  );
}
